import * as React from 'react';
import { cn } from 'v5/platform/dom/cn';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { focusRings } from 'v5/design-system/_common/focusRing';

export interface OverflowRenderProps {
  numVisible: number;
  numOverflow: number;
  numTotal: number;
  /** The default overflow button element */
  defaultButton: React.ReactElement;
}

export interface OverflowContainerProps {
  children: React.ReactNode;
  className?: string;
  overflowButtonClassName?: string;
  overflowButtonLabel?: (count: number) => string;
  /**
   * Layout of items inside the overflow popover.
   * `wrap` — horizontal chips (default). `stack` — one item per row (e.g. links).
   */
  overflowPopoverVariant?: 'wrap' | 'stack';
  /** Custom render function for overflow indicator. If provided, takes full control of overflow UI */
  renderOverflow?: (props: OverflowRenderProps) => React.ReactNode;
}

export const OverflowContainer = ({
  children,
  className,
  overflowButtonClassName,
  overflowButtonLabel = count => `+${count} more`,
  overflowPopoverVariant = 'wrap',
  renderOverflow
}: OverflowContainerProps) => {
  const internalContainerRef = React.useRef<HTMLDivElement>(null);
  const measureContainerRef = React.useRef<HTMLDivElement>(null);
  const actualOverflowButtonRef = React.useRef<HTMLElement | null>(null);
  const overflowButtonWidthRef = React.useRef<number>(0);
  const firstElementRef = React.useRef<HTMLDivElement | null>(null);
  const [visibleCount, setVisibleCount] = React.useState<number>(0);
  const [overflowItems, setOverflowItems] = React.useState<React.ReactNode[]>(
    []
  );
  const [firstElementHasOverflow, setFirstElementHasOverflow] =
    React.useState(false);
  const [firstElementContent, setFirstElementContent] =
    React.useState<string>('');
  const childrenArray = React.useMemo(
    () => React.Children.toArray(children),
    [children]
  );
  const childrenCount = childrenArray.length;
  const isCalculatingRef = React.useRef(false);

  React.useEffect(() => {
    const GAP = 8;

    const calculateOverflow = () => {
      if (isCalculatingRef.current) return;
      const container = internalContainerRef.current;
      const measureContainer = measureContainerRef.current;
      if (!container || !measureContainer) return;

      isCalculatingRef.current = true;

      // Measure actual overflow button width if it exists (from previous render)
      // If not available yet, use last measured width or fallback estimate
      const buttonWidth =
        actualOverflowButtonRef.current?.offsetWidth ||
        overflowButtonWidthRef.current ||
        90;

      const children = Array.from(measureContainer.children) as HTMLElement[];
      const containerWidth = container.offsetWidth;
      const allChildrenWidth = measureContainer.offsetWidth;

      let calculatedVisibleCount: number;
      if (children.length === 0 || allChildrenWidth <= containerWidth) {
        calculatedVisibleCount = children.length;
      } else {
        calculatedVisibleCount = 0;
        let totalWidth = 0;
        for (let i = 0; i < children.length; i++) {
          const widthWithChild =
            totalWidth +
            (calculatedVisibleCount > 0 ? GAP : 0) +
            children[i].offsetWidth;
          const needsOverflowButton = i < children.length - 1;
          const wouldExceed = needsOverflowButton
            ? widthWithChild + GAP + buttonWidth > containerWidth
            : widthWithChild > containerWidth;

          if (wouldExceed) break;

          totalWidth = widthWithChild;
          calculatedVisibleCount++;
        }
        if (calculatedVisibleCount === 0) calculatedVisibleCount = 1;
      }

      const hasOverflow = calculatedVisibleCount < children.length;
      setVisibleCount(calculatedVisibleCount);
      setOverflowItems(
        hasOverflow ? childrenArray.slice(calculatedVisibleCount) : []
      );

      // Check if first element has text overflow (for tooltip)
      requestAnimationFrame(() => {
        if (firstElementRef.current) {
          const innerChild = firstElementRef.current
            .firstElementChild as HTMLElement;
          const hasOverflow = innerChild?.scrollWidth > innerChild?.clientWidth;
          setFirstElementHasOverflow(hasOverflow);
          if (hasOverflow) {
            setFirstElementContent(innerChild.textContent || '');
          }
        } else {
          setFirstElementHasOverflow(false);
        }
      });

      isCalculatingRef.current = false;
    };

    const scheduleCalculate = () => requestAnimationFrame(calculateOverflow);
    const rafId = scheduleCalculate();
    const resizeObserver = new ResizeObserver(scheduleCalculate);

    if (internalContainerRef.current) {
      resizeObserver.observe(internalContainerRef.current);
    }

    // Also observe the actual overflow button if it exists
    if (actualOverflowButtonRef.current) {
      resizeObserver.observe(actualOverflowButtonRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [childrenCount, childrenArray]);

  const shouldShowButton = visibleCount < childrenArray.length;

  return (
    <div
      ref={internalContainerRef}
      className={cn(
        'relative overflow-hidden flex items-center gap-2',
        className
      )}
    >
      <div
        ref={measureContainerRef}
        className="absolute invisible flex items-center gap-2"
        aria-hidden="true"
      >
        {childrenArray.map((child, index) => (
          <div key={index} className="inline-block shrink-0">
            {child}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 min-w-0">
        {childrenArray.map((child, index) => {
          const isVisible = index < visibleCount;

          if (index === 0) {
            return (
              <SimpleTooltip
                key={index}
                title={firstElementHasOverflow ? firstElementContent : null}
              >
                <div
                  ref={firstElementRef}
                  className={cn(
                    'min-w-0 shrink cursor-default focus-visible:rounded-sm',
                    firstElementHasOverflow && focusRings.focus.visible
                  )}
                  tabIndex={firstElementHasOverflow ? 0 : undefined}
                  role={firstElementHasOverflow ? 'button' : undefined}
                >
                  {child}
                </div>
              </SimpleTooltip>
            );
          }

          return (
            <div
              key={index}
              className={cn('inline-block shrink-0', !isVisible && 'hidden')}
            >
              {child}
            </div>
          );
        })}
      </div>

      {shouldShowButton &&
        (() => {
          const defaultButton = (
            <button
              ref={el => {
                // Store reference to actual button for width measurement
                if (el) {
                  actualOverflowButtonRef.current = el;
                  if (el.offsetWidth > 0) {
                    overflowButtonWidthRef.current = el.offsetWidth;
                  }
                } else {
                  actualOverflowButtonRef.current = null;
                }
              }}
              className={cn(
                'inline-flex h-7 shrink-0 items-center rounded-full border border-border-default bg-bg-default px-3 text-xs font-medium transition-colors hover:bg-bg-subtle hover:text-fg-default ml-1',
                focusRings.focus.visible,
                overflowButtonClassName
              )}
              onClick={e => {
                e.stopPropagation();
              }}
            >
              {overflowButtonLabel(overflowItems.length)}
            </button>
          );

          if (renderOverflow) {
            return renderOverflow({
              numVisible: visibleCount,
              numOverflow: overflowItems.length,
              numTotal: childrenArray.length,
              defaultButton
            });
          }

          return (
            <Popover>
              <PopoverTrigger asChild>{defaultButton}</PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="center"
                className="max-w-[400px] p-3"
              >
                <div
                  className={
                    overflowPopoverVariant === 'stack'
                      ? 'flex flex-col gap-2'
                      : 'flex flex-wrap gap-2'
                  }
                >
                  {overflowItems.map((item, index) => (
                    <div
                      key={index}
                      className={
                        overflowPopoverVariant === 'stack'
                          ? 'min-w-0'
                          : 'inline-block shrink-0'
                      }
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          );
        })()}
    </div>
  );
};
